import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

export const colors = {
  primaryBlue: '#000000',
  lightBlue: '#65B4FE',
  darkBlue: '#051021',

  primaryGreen: '#00C0B5',
  lightGreen: '#65FFC8',

  primaryYellow: '#FFEA9E',
  primaryRed: '#FF8291',

  neutralWhite: '#FFFFFF',
  neutralBlack: '#000000',

  primaryGrey: '#9BA3AE',
  lightGrey: '#ADB5BF',
  grey: '#E0E3E9',
  greyBackground: '#F7F8FA',
  greySurface: '#F0F2F5',
  darkGrey: '#353D49',
  silverGrey: '#EBEDF0',
  greyHeartbeatBackground: '#f0f0f0',
  greyHeartbeatBorder: '#e0e0e0',

  orange: '#ffcc99',
};

export const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    common: {
      black: colors.neutralBlack,
      white: colors.neutralWhite,
    },
    primary: {
      main: '#e3de56',
      dark: '#262726',
      light: '#e3de56',
    },
    secondary: {
      main: colors.primaryGreen,
      light: colors.primaryGrey,
    },
    success: {
      main: colors.primaryGreen,
    },
    warning: {
      main: colors.orange,
    },
    info: {
      main: colors.lightBlue,
    },
    error: {
      main: colors.primaryRed,
      light: colors.orange,
    },
    text: {
      primary: colors.darkGrey,
      secondary: colors.primaryGrey,
    },
    grey: {
      100: colors.lightGrey,
      200: colors.grey,
      300: colors.greyBackground,
      400: colors.darkGrey,
      500: colors.primaryGrey,
      600: colors.greySurface,
      700: colors.silverGrey,
      800: colors.greyHeartbeatBackground,
      900: colors.greyHeartbeatBorder,
    },
    divider: colors.grey,
    background: {
      default: colors.neutralWhite,
      paper: colors.greySurface,
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: 'Open Sans',
    fontWeightRegular: '400',
    fontWeightMedium: '600',
    fontWeightBold: '700',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        loading: {
          color: colors.darkGrey,
        },
        noOptions: {
          color: colors.darkGrey,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.darkGrey,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: 'Open Sans',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: colors.neutralWhite,
          fontSize: 12,
          cursor: 'pointer',
          borderRadius: 8,
          padding: 8,
        },
        arrow: {
          color: 'rgba(0, 0, 0, 0.8)',
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        density: 'comfortable',
        disableColumnMenu: true,
        disableColumnFilter: true,
        disableColumnSelector: true,
        disableRowSelectionOnClick: true,
        autoHeight: false,
        columnHeaderHeight: 40,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid #50514f`,
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(1),
          borderRadius: theme.shape.borderRadius,
          width: '100%',
          '&:focus': {
            outline: 'none',
          },
          '&:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
        }),
        cell: () => ({
          backgroundColor: 'transparent',
          minHeight: 60,
          alignContent: 'center',
          borderBottom: `1px solid #E0E3E9`,
          '&:focus': {
            outline: 'none',
          },
          '&:focus-within': {
            outline: 'none',
          },
        }),
        columnHeader: ({ theme }) => ({
          backgroundColor: '#F7F8FA',
          color: '#50514f',
          fontWeight: theme.typography.fontWeightBold,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          borderBottom: `1px solid #50514f`,
          '&:focus': {
            outline: 'none',
          },
          '&:focus-within': {
            outline: 'none',
          },
        }),
        virtualScroller: {
          overflow: 'auto !important',
          '&::-webkit-scrollbar': {
            width: 1,
            height: 1,
            display: 'block',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.2)',
            borderRadius: 0.5,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        },
        row: {
          '&:hover': {
            backgroundColor: 'rgba(227, 222, 86, 0.15) !important',
            cursor: 'pointer',
            '& .MuiDataGrid-cell': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
    },
    MuiTextField: {
      variants: [
        {
          props: { className: 'form-text-field' },
          style: ({ theme }: { theme: Theme }) => ({
            '& .MuiInputBase-root': {
              height: theme.spacing(4),
              backgroundColor: theme.palette.background.default,
              fontSize: theme.typography.body2.fontSize,
              fontFamily: theme.typography.fontFamily,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.grey[200],
                borderWidth: 1,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.grey[200],
                borderWidth: 1,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: 1,
              },
              '&.Mui-error fieldset': {
                borderColor: theme.palette.error.main,
                borderWidth: 1,
              },
            },
            '& .MuiInputBase-input': {
              color: theme.palette.text.primary,
              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 1,
              },
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              outline: 'none',
            },
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          }),
        },
      ],
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '&.form-select-field': {
            height: theme.spacing(4),
            fontSize: theme.typography.body2.fontSize,
            fontFamily: theme.typography.fontFamily,

            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              padding: `0 ${theme.spacing(1.75)}`,
            },

            '& .MuiSelect-select.Mui-disabled': {
              fontSize: theme.typography.body2.fontSize,
              fontFamily: theme.typography.fontFamily,
            },

            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
            },

            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
            },
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '&.form-menu-item': {
            fontSize: theme.typography.body2.fontSize,
            fontFamily: theme.typography.fontFamily,
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          '&.Mui-selected': {
            color: '#000000',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#e3de56',
          height: 3,
        },
      },
    },
  },
};

const defaultTheme = createTheme(themeOptions);

export default defaultTheme;
